/// <reference path="../apollo-types.d.ts" />
import { ApolloClient, ApolloLink } from "@apollo/client";
import { SetContextLink } from "@apollo/client/link/context";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import DefaultCache from "@repo/graphql/cache";
import { backendUrl, webSocketUrl } from "@repo/graphql/cache/url";
import UploadHttpLink from "apollo-upload-client/UploadHttpLink.mjs";
import AwaitLock from "await-lock";
import axios from "axios";
import { File as FileSystemFile } from "expo-file-system";
import { createClient } from "graphql-ws";
import { AppState, Platform } from "react-native";
import ReactNativeFile from "./file";
import {
  getRefreshToken,
  getToken,
  hasTokenExpired,
  logIn,
  logOut,
  registerWebSocketTerminator,
  wsGeneration,
} from "./states";
import { base64ToFile, blobUrlToFile } from "./utils";
import { toWellFormedVariables } from "./wellFormedText";

const lock = new AwaitLock();

const rawRefresh = `
mutation refreshToken($refreshToken: String!) {
  user {
    refreshToken(refreshToken: $refreshToken) {
      payload
      refreshExpiresIn
      token
      refreshToken
      __typename
    }
  }
}`;

const url = `${backendUrl}/graphql/`;

const refreshAuthToken = async () => {
  const refreshToken = await getRefreshToken();
  const body = {
    operationName: "refreshToken",
    variables: {
      refreshToken,
    },
    query: rawRefresh,
  };
  const refreshResponse = await axios(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: body,
  });
  return refreshResponse.data?.data?.user?.refreshToken;
};

const getValidToken = async () => {
  await lock.acquireAsync();
  try {
    const token = await getToken();
    if (!token) return null;
    if (await hasTokenExpired()) {
      // No refresh token (impersonation): the session ends with the token.
      if (!(await getRefreshToken())) {
        await logOut();
        return null;
      }
      const refreshedToken = await refreshAuthToken();
      if (refreshedToken) {
        await logIn(refreshedToken);
        return await getToken();
      }
      logOut();
    }
    return token;
  } finally {
    lock.release();
  }
};

// Repairs lone UTF-16 surrogates left behind by the Lexical-based markdown
// editor before any request leaves the client, on every path (http/upload and
// the websocket subscription path the split below fans out to). Placed first
// in the chain so nothing downstream ever sees an unencodable string. See
// `wellFormedText.ts` for why this is needed.
const sanitizeVariablesLink = new ApolloLink((operation, forward) => {
  const sanitized = toWellFormedVariables(operation.variables);
  if (sanitized !== operation.variables) operation.variables = sanitized;
  return forward(operation);
});

const httpLink = new UploadHttpLink({
  uri: url,
  credentials: "include",
  headers: {
    "Accept-Language": "es",
  },
  isExtractableFile: (file): file is ReactNativeFile =>
    file instanceof ReactNativeFile,
  formDataAppendFile: (formData, fieldName, file: ReactNativeFile) => {
    if (Platform.OS === "web") {
      const resolved = file.uri.startsWith("blob:")
        ? blobUrlToFile(file.uri, file.name, file.type)
        : base64ToFile(file.uri, file.name, file.type);
      formData.append(fieldName, resolved);
      return;
    }
    // Native: Expo's winter runtime replaces the global `fetch`, and its
    // multipart serializer (convertFormDataAsync) does NOT understand React
    // Native's proprietary `{ uri, name, type }` file descriptors — it only
    // handles strings, Blobs, and File-like objects exposing `bytes()`.
    // Appending a `ReactNativeFile` directly throws "Unsupported FormDataPart
    // implementation". Wrap the local file in an expo-file-system `File`,
    // which implements Blob (with `bytes()`, `name`, and `type`) so the body
    // can be built from the file's contents.
    formData.append(fieldName, new FileSystemFile(file.uri));
  },
});

// How often we ping, and how long we wait for the pong before declaring the
// socket dead. The server answers pings in `MontuGraphQLSubscriptionHandler`; if
// it ever stops, this watchdog will close-loop, which is the loud failure we want.
//
// Native gets a much longer interval than web: `GamificationListener` and
// `useListenToNewChat` mount app-wide, so every phone with the app open is
// pinging on this cadence forever, and each ping wakes the radio. 30s is three
// times fewer wake-ups than the previous 10s while still catching a half-open
// socket well within the time a user would notice stale data.
const KEEP_ALIVE_MS = Platform.OS === "web" ? 10_000 : 30_000;
const PONG_TIMEOUT_MS = 5_000;

// graphql-ws retries with an UNCAPPED exponential backoff — 2^n seconds, forever.
// With a bounded `retryAttempts` that eventually gives up and surfaces an error, but
// we retry indefinitely (a chat has to survive a tunnel), so the delay must be
// capped or a user who racks up ~20 failed reconnects on a flaky network would wait
// days for the 21st, with every subscription in the app silently dead in the
// meantime.
const MAX_RETRY_WAIT_MS = 10_000;

let pongTimer: ReturnType<typeof setTimeout> | undefined;

// Backgrounded apps (especially Android) throttle JS timers, so the pong
// handler can easily miss the `PONG_TIMEOUT_MS` window on a socket that is
// perfectly healthy — the OS just hasn't scheduled our callback. Track
// foreground state so the watchdog only arms while we can actually trust the
// timer to fire on time; a background app keeps its (possibly stale) socket
// instead of self-terminating and forcing a reconnect + re-auth handshake.
//
// This is a single listener for the app's one module-scoped client, never
// torn down — same lifetime as `wsClient` below, so there is nothing to leak.
let isAppActive = AppState.currentState === "active";
AppState.addEventListener("change", (nextState) => {
  isAppActive = nextState === "active";
  if (!isAppActive) clearTimeout(pongTimer);
});

// One socket for the whole app, created lazily on the first subscribe.
//
// This used to be built per operation, inside the link's request function, which
// meant every useSubscription opened its own socket and its own auth handshake —
// and left nothing to hang a reconnect signal off.
const wsClient = createClient({
  url: `${webSocketUrl}/graphql/`,
  connectionParams: async () => ({
    Authorization: await getValidToken(),
  }),
  retryAttempts: Infinity,
  shouldRetry: () => true,
  retryWait: async (retries) => {
    const backoff = Math.min(2 ** retries * 1000, MAX_RETRY_WAIT_MS);
    const jitter = Math.floor(Math.random() * 700) + 300;
    await new Promise((resolve) => setTimeout(resolve, backoff + jitter));
  },
  // graphql-ws will not act on an unanswered ping by itself: a half-open socket
  // (device sleep, wifi -> LTE, an idle timeout at the load balancer) stays
  // `OPEN` forever and fires no close event, so the retry logic never runs. The
  // watchdog below is what turns that silence into a close, and therefore into a
  // reconnect.
  keepAlive: KEEP_ALIVE_MS,
  on: {
    connected: () => {
      clearTimeout(pongTimer);
      wsGeneration(wsGeneration() + 1);
    },
    closed: () => clearTimeout(pongTimer),
    ping: (received) => {
      if (received) return; // a ping *from* the server; graphql-ws pongs it for us
      if (!isAppActive) return; // timers are throttled in the background; don't arm a watchdog we can't trust
      pongTimer = setTimeout(() => {
        // No pong came back. Close it ourselves so the retry logic kicks in.
        wsClient.terminate();
      }, PONG_TIMEOUT_MS);
    },
    pong: (received) => {
      if (received) clearTimeout(pongTimer);
    },
  },
});

// Signing in or out must drop the socket: it authenticates once, in
// `connection_init`, and the server pins the connection to that user for its life.
registerWebSocketTerminator(() => wsClient.terminate());

const wsLink = new GraphQLWsLink(wsClient);

const splitLink = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  httpLink as unknown as ApolloLink,
);

// Which app is calling, as the backend's `ClientAppEnum`. Unlike the app
// label (the tenant: raindoor, kinegun, montu), it tells mobil from center.
export type ClientApp =
  "mobil" | "center" | "neon" | "neon_flow" | "creator_portal";

const authLink = (appLabel: string, clientApp: ClientApp) =>
  new SetContextLink(async (prevContext) => {
    const { headers } = prevContext;
    const defaultHeaders = {
      ...headers,
      "Accept-Language": "es",
      "X-App-Label": appLabel,
      "X-Client-App": clientApp,
      "X-Client-Platform": Platform.OS,
    };
    const token = await getValidToken();
    if (!token) {
      return {
        headers: defaultHeaders,
      };
    }
    return {
      headers: {
        ...defaultHeaders,
        Authorization: `JWT ${token}`,
      },
    };
  });

const client = (appLabel: string, clientApp: ClientApp) =>
  new ApolloClient({
    link: sanitizeVariablesLink
      .concat(authLink(appLabel, clientApp))
      .concat(splitLink),
    cache: DefaultCache(),
    defaultOptions: {
      watchQuery: {
        refetchWritePolicy: "merge",
        notifyOnNetworkStatusChange: true,
      },
    },
  });

export default client;
